<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables"
import type { Services } from "@services"
import { ServiceError } from "@core/exceptions"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            email: "E-mail",
            sendCode: "Envoyer le code de validation",
            errorUserNotFound: "Aucun compte trouvé pour cette adresse e-mail.",
            errorAlreadyValidated: "Ce compte est déjà validé. Connectez-vous.",
            errorUnknown: "Une erreur est survenue. Veuillez réessayer.",
            message:
                "Veuillez renseigner l'adresse e-mail utilisée lors de votre inscription pour recevoir un code de confirmation. Si vous l'avez oublié, vérifiez le mail de confirmation reçu après le paiement.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
/** Payload émis après envoi du code (données utilisateur pour l'étape confirmation). */
export type FormAuthFirstLoginEmailNextPayload = {
    _id?: string
    username?: string
    [key: string]: unknown
}

const emit = defineEmits<{
    (e: "next", payload?: FormAuthFirstLoginEmailNextPayload): void
}>()

// ================================
// COMPOSABLES
// ================================
const { $sendUserAccountValidationMail } = inject<Services>("services")!

// ================================
// STATE
// ================================
/** E-mail saisi */
const email = ref("")
/** Message d'erreur affiché */
const errorMessage = ref("")

/** Regex simple pour valider le format e-mail */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** E-mail valide (format) */
const isEmailValid = computed(() => EMAIL_REGEX.test(email.value.trim()))

/** Wrapper d'appel sendUserAccountValidationMail avec gestion chargement et erreurs */
const sendCodeFetch = useFetchWrapper(
    async () => {
        errorMessage.value = ""
        const data = await $sendUserAccountValidationMail({
            userName: email.value.trim(),
        })
        emit("next", data as FormAuthFirstLoginEmailNextPayload)
    },
    (err: Error) => {
        const e = err as ServiceError
        switch (e.code) {
            case "invalid_user_already_validated":
                errorMessage.value = t("errorAlreadyValidated")
                break
            case "not_found_user":
                errorMessage.value = t("errorUserNotFound")
                break
            default:
                errorMessage.value = t("errorUnknown")
        }
    }
)

// ================================
// INTERACTIONS
// ================================
/** Envoie le code de validation par e-mail puis émet next en cas de succès. */
function onSubmit(): void {
    if (!isEmailValid.value) return
    sendCodeFetch.fetch()
}
</script>

<template>
    <form class="flex flex-col gap-4" @submit.prevent="onSubmit">
        <p class="text-sm text-surface-600">
            {{ t("message") }}
        </p>
        <BaseInputWrapper input-id="auth-first-login-email" :label="t('email')">
            <BaseInputText
                id="auth-first-login-email"
                v-model="email"
                type="email"
                class="w-full"
            />
        </BaseInputWrapper>
        <BaseButton
            type="submit"
            :label="t('sendCode')"
            :disabled="!isEmailValid || sendCodeFetch.isFetching.value"
            :loading="sendCodeFetch.isFetching.value"
        />
        <BaseMessage v-if="errorMessage" severity="error">
            {{ errorMessage }}
        </BaseMessage>
    </form>
</template>
